import { Vector2, Vector3 } from 'three';

import {
  Conic,
  getConicPoint,
  intersectConicWithLine2d,
} from '../../elements/design/design-node/utils/conic';
import {
  areVectorsParallel2d,
  checkLine2dIntersection,
  findClosestPointOnRay,
} from '../../utilities/math-helpers';

/**
 * Distance treshold, if the new point is closer than this distance
 * we will use to existing geometry.
 */
const distanceTreshold = 0.001;

export type TrajectorySnapTarget = {
  /** The hovered position on the geometry. */
  position: Vector3;
} & (
  | { type: 'point' }
  | { type: 'line'; start: Vector3; end: Vector3 }
  | { type: 'curve'; conic: Conic }
  | { type: 'face' }
);

export type TrajectorySnapResult = {
  point: Vector3;
  /** True if the point should be attached to the target geometry, otherwise it is a stand-alone point. */
  attached: boolean;
  /** The line that the point is placed on, if it is where the trajectory crosses a line. */
  crossedLine?: { start: Vector3; end: Vector3 };
};

/**
 * Snaps hovered geometry onto a trajectory going from origin in the given direction in the 2d plane,
 * so that the new line keeps its direction (e.g. parallel or perpendicular) when snapping to geometry.
 *
 * - Point: Attached if it is on the trajectory, otherwise projected onto the trajectory.
 * - Line: Placed where the trajectory crosses the line. Attached if the crossing is on the line segment,
 *   otherwise it is a stand-alone point on the extension of the line segment.
 * - Curve: Placed where the trajectory crosses the curve, at the crossing closest to the hovered position.
 *   If it does not cross the arc on screen, it is handled like a face.
 * - Face: Projected onto the trajectory.
 *   This is also done for lines that are parallel to the trajectory, since it never crosses them,
 *   and for lines where the crossing is not on screen, since the point would jump far away from the cursor.
 *
 * Returns undefined if the trajectory runs along the line, since the point would overlap it.
 */
export const snapToTrajectory = (
  origin: Vector3,
  direction: Vector3,
  target: TrajectorySnapTarget,
  isOnScreen: (p: Vector3) => boolean,
): TrajectorySnapResult | undefined => {
  const trajectory = direction.clone().setZ(0).normalize();

  /**
   * Projects a point onto the trajectory in the 2d plane, keeping the height of the point.
   */
  const projectOnTrajectory = (p: Vector3) =>
    findClosestPointOnRay(origin.clone().setZ(p.z), trajectory, p);

  if (target.type === 'line') {
    const { start, end } = target;

    if (!areVectorsParallel2d(trajectory, end.clone().sub(start))) {
      const intersection = checkLine2dIntersection(
        { start: origin, end: origin.clone().add(trajectory) },
        { start, end },
      );

      const crossing = new Vector3(intersection.x, intersection.y, intersection.zLine2);

      if (isOnScreen(crossing)) {
        return { point: crossing, attached: intersection.onLine2, crossedLine: { start, end } };
      }
    } else if (projectOnTrajectory(start).distanceTo(start) < distanceTreshold) {
      return undefined;
    }
  }

  if (target.type === 'curve') {
    const crossing = findCurveCrossing(origin, trajectory, target, isOnScreen);

    if (crossing) {
      return { point: crossing, attached: true };
    }
  }

  const projectedPoint = projectOnTrajectory(target.position);

  if (target.type === 'point' && projectedPoint.distanceTo(target.position) < distanceTreshold) {
    return { point: target.position.clone(), attached: true };
  }

  return { point: projectedPoint, attached: false };
};

/**
 * Finds where the line through origin in the direction crosses the curve in the 2d plane,
 * the crossing closest to the hovered position is returned with the height of the curve.
 */
const findCurveCrossing = (
  origin: Vector3,
  direction: Vector3,
  target: { position: Vector3; conic: Conic },
  isOnScreen: (p: Vector3) => boolean,
): Vector3 | undefined => {
  const crossings = intersectConicWithLine2d(
    target.conic,
    origin,
    new Vector2(direction.x, direction.y),
  )
    .map((t) => getConicPoint(target.conic, t))
    .filter(isOnScreen);

  // Closest in the horizontal plane, since the hovered position can be at another height than the
  // curve at the crossings.
  const distanceTo = (p: Vector3) => Math.hypot(p.x - target.position.x, p.y - target.position.y);

  return crossings.sort((a, b) => distanceTo(a) - distanceTo(b))[0];
};
